import {
  Stack,
  TextField,
  useTheme,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Box,
  Divider,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fade,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  CardHeader,
  InputAdornment,
  Select,
  MenuItem,
  DialogContent,
  Zoom,
  Autocomplete,
  Popover,
  Switch,
  FormGroup,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import ReactHtmlParser from 'react-html-parser';
import 'react-quill/dist/quill.snow.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@emotion/styled';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ImageIcon from '@mui/icons-material/Image';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import imagepreview from '../../media/bannerup.png';
import NoimageFound from '../../media/noimage.jpg';
import axios from '../../axios';
import './animation.css';

const useStyles = styled((theme) => ({
  fileInput: {
    display: 'none',
  },
}));

const HeaderPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  borderRadius: '12px',
  padding: '16px 24px',
  marginBottom: '16px',
  boxShadow: '0 4px 20px rgba(19, 78, 94, 0.3)',
}));

const BlogCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  border: '1px solid #e8ecf0',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
    '& .blog-overlay': {
      opacity: 1,
    },
  },
}));

const BlogOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  background: 'rgba(0,0,0,0.5)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  backdropFilter: 'blur(4px)',
  zIndex: 5,
  pointerEvents: 'none',
  '& .MuiIconButton-root': {
    pointerEvents: 'auto',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontWeight: 700,
  textAlign: 'left',
  backgroundColor: '#1a3a4a',
  padding: '8px 12px',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  letterSpacing: '0.03em',
  borderBottom: '2px solid rgba(255,255,255,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    transition: 'background-color 0.2s ease',
  },
}));

const StickyTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
}));

// Image Preview Dialog Component
const FullscreenImageDialog = ({ open, imageUrl, onClose, title }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          p: 4,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {title && (
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              position: 'absolute',
              top: 20,
              left: 50,
              fontWeight: 600,
              zIndex: 10,
            }}
          >
            {title}
          </Typography>
        )}

        <Zoom in={open} timeout={400}>
          <Box
            component="img"
            src={imageUrl || NoimageFound}
            alt="Preview"
            sx={{
              maxWidth: '95%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </Zoom>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            mt: 3,
            letterSpacing: '0.1em',
          }}
        >
          Click anywhere outside the image or press ESC to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

// Quill modules configuration to prevent empty paragraphs
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'video'],
    [{ align: [] }],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

// Helper function to clean HTML content
const cleanHtmlContent = (html) => {
  if (!html) return '';
  // Remove empty paragraphs at the beginning
  let cleaned = html.replace(/^(<p><br\s*\/?><\/p>)+/, '');
  // Remove empty paragraphs at the end
  cleaned = cleaned.replace(/(<p><br\s*\/?><\/p>)+$/, '');
  // Remove empty paragraphs that only contain &nbsp; or whitespace
  cleaned = cleaned.replace(/<p>\s*(&nbsp;|\s)*\s*<\/p>/g, '');
  return cleaned;
};

const Blogs = () => {
  const tokent =
    'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ';
  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [open, setOpen] = useState(false);
  const [openAlert, setopenAlert] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [operation, setOperation] = useState('Add');
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [openLoader, setOpenLoader] = useState(false);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorWrapperRef = useRef(null);
  const [viewMode, setViewMode] = useState('grid');

  // Filter states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [scheduleFilter, setScheduleFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Image preview
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageTitle, setPreviewImageTitle] = useState('');

  // Form fields
  const [BlogTitle, setBlogTitle] = useState('');
  const [Category, setCategory] = useState('');
  const [ProductID, setProductID] = useState('');
  const [productList, setProductList] = useState([]);
  const [selectedImageFile, setSelectedImageFile] = useState('');
  const [BlogDescription, setBlogDescription] = useState('');
  const [Summary, setSummary] = useState('');
  const [ActiveStatus, setActiveStatus] = useState('Y');
  const [IsScheduled, setIsScheduled] = useState('N');
  const [ScheduledDateTime, setScheduledDateTime] = useState('');

  const [imagePreview, setImagePreview] = useState('');
  const [existingImageName, setExistingImageName] = useState('');
  const [blogData, setBlogData] = useState([]);
  const [IdToDelete, setIdToDelete] = useState(null);
  const [editID, setEditID] = useState();

  // Validation state - only track touched fields
  const [touchedFields, setTouchedFields] = useState({});
  const [errors, setErrors] = useState({});

  const filterOptions = useMemo(
    () => ({
      status: [
        { label: 'All Status', value: 'all' },
        { label: 'Active', value: 'Y' },
        { label: 'Inactive', value: 'N' },
      ],
      schedule: [
        { label: 'All', value: 'all' },
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Not Scheduled', value: 'unscheduled' },
      ],
    }),
    []
  );

  // Validation function
  const validateField = useCallback((name, value, formState) => {
    const { IsScheduled: scheduled } = formState;
    let error = '';

    switch (name) {
      case 'BlogTitle':
        if (!value?.trim()) error = 'Title is required';
        else if (value.length > 100) error = 'Cannot exceed 100 characters';
        break;
      case 'Category':
        if (!value?.trim()) error = 'Category is required';
        else if (value.length > 50) error = 'Cannot exceed 50 characters';
        break;
      case 'selectedImageFile':
        if (!value) error = 'Please upload an image';
        break;
      case 'BlogDescription':
        if (!value?.trim()) error = 'Blog description is required';
        break;
      case 'Summary':
        if (!value?.trim()) error = 'Summary is required';
        break;
      case 'ScheduledDateTime':
        if (scheduled === 'Y' && !value) error = 'Please select publish date and time';
        break;
      default:
        break;
    }

    return error;
  }, []);

  // Validate all fields
  const validateAllFields = useCallback(
    (formState) => {
      const fields = ['BlogTitle', 'Category', 'selectedImageFile', 'BlogDescription', 'Summary'];
      if (formState.IsScheduled === 'Y') fields.push('ScheduledDateTime');

      const newErrors = {};
      fields.forEach((field) => {
        const value = formState[field];
        const error = validateField(field, value, formState);
        if (error) newErrors[field] = error;
      });
      return newErrors;
    },
    [validateField]
  );

  // Handle field change with validation
  const handleFieldChange = useCallback(
    (name, value) => {
      const formState = {
        BlogTitle,
        Category,
        selectedImageFile,
        BlogDescription,
        Summary,
        IsScheduled,
        ScheduledDateTime,
      };

      // Update field
      switch (name) {
        case 'BlogTitle':
          setBlogTitle(value);
          break;
        case 'Category':
          setCategory(value);
          break;
        case 'ProductID':
          setProductID(String(value ?? ''));
          break;
        case 'Summary':
          setSummary(value);
          break;
        case 'ActiveStatus':
          setActiveStatus(value);
          break;
        case 'IsScheduled':
          setIsScheduled(value);
          if (value === 'N') setScheduledDateTime('');
          break;
        case 'ScheduledDateTime':
          setScheduledDateTime(value);
          break;
        default:
          break;
      }

      // Update touched status
      setTouchedFields((prev) => ({ ...prev, [name]: true }));

      // Validate field
      const updatedFormState = { ...formState, [name]: value };
      const error = validateField(name, value, updatedFormState);
      setErrors((prev) => ({ ...prev, [name]: error }));
    },
    [BlogTitle, Category, selectedImageFile, BlogDescription, Summary, IsScheduled, ScheduledDateTime, validateField]
  );

  const handleBlur = useCallback(
    (event) => {
      const { name, value } = event.target;
      setTouchedFields((prev) => ({ ...prev, [name]: true }));
      const error = validateField(name, value, {
        BlogTitle,
        Category,
        selectedImageFile,
        BlogDescription,
        Summary,
        IsScheduled,
        ScheduledDateTime,
      });
      setErrors((prev) => ({ ...prev, [name]: error }));
    },
    [BlogTitle, Category, selectedImageFile, BlogDescription, Summary, IsScheduled, ScheduledDateTime, validateField]
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setTouchedFields((prev) => ({ ...prev, selectedImageFile: true }));
      const error = validateField('selectedImageFile', file, {
        BlogTitle,
        Category,
        selectedImageFile: file,
        BlogDescription,
        Summary,
        IsScheduled,
        ScheduledDateTime,
      });
      setErrors((prev) => ({ ...prev, selectedImageFile: error }));
    }
  };

  const handleChange2 = (value) => {
    // Clean the HTML content before setting
    const cleanedValue = cleanHtmlContent(value);
    setBlogDescription(cleanedValue);
    setTouchedFields((prev) => ({ ...prev, BlogDescription: true }));
    const error = validateField('BlogDescription', cleanedValue, {
      BlogTitle,
      Category,
      selectedImageFile,
      BlogDescription: cleanedValue,
      Summary,
      IsScheduled,
      ScheduledDateTime,
    });
    setErrors((prev) => ({ ...prev, BlogDescription: error }));
  };

  const resetFields = useCallback(() => {
    setBlogTitle('');
    setCategory('');
    setProductID('');
    setSelectedImageFile('');
    setExistingImageName('');
    setBlogDescription('');
    setSummary('');
    setActiveStatus('Y');
    setIsScheduled('N');
    setScheduledDateTime('');
    setImagePreview('');
    setTouchedFields({});
    setErrors({});
  }, []);

  const formatDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  // API calls
  const getActiveProducts = useCallback(async () => {
    try {
      const res = await axios.instance.get('/GetallActiveProduct', {
        headers: { Authorization: tokent, 'Content-Type': 'application/json' },
      });
      setProductList(res.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  const getAllBlog = useCallback(async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get('/getAllBlog', {
        headers: { Authorization: tokent, 'Content-Type': 'application/json' },
      });
      setBlogData(res.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setOpenLoader(false);
    }
  }, []);

  useEffect(() => {
    getAllBlog();
    getActiveProducts();
  }, [getAllBlog, getActiveProducts]);

  const InsertBlog = async (newUser) => {
    try {
      await axios.instance.post('/InsertBlog', newUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      await getAllBlog();
      setAlertType('success');
      setAlertMessage('New Blog Added Successfully!');
      setopenAlert(true);
    } catch (error) {
      console.error('Error adding Blog:', error);
      setAlertType('error');
      setAlertMessage('Failed to add the Blog.');
      setopenAlert(true);
    }
  };

  const UpdateBlog = async (userId, updatedUser) => {
    try {
      await axios.instance.put(`/UpdateBlog/${userId}`, updatedUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      await getAllBlog();
      setAlertType('success');
      setAlertMessage('Blog Updated Successfully!');
      setopenAlert(true);
    } catch (error) {
      console.error('Error updating Blog:', error);
      setAlertType('error');
      setAlertMessage('Failed to update the Blog.');
      setopenAlert(true);
    }
  };

  const DeleteBlog = async (userId) => {
    try {
      const res = await axios.instance.delete(`/DeleteBlog/${userId}`, {
        headers: { Authorization: tokent, 'Content-Type': 'application/json' },
      });
      await getAllBlog();
      setOpenDelete(false);
      if (res.data === '') {
        setAlertType('warning');
        setAlertMessage('Blog Deleted Successfully!');
      } else {
        setAlertType('error');
        setAlertMessage("Oops! Can't delete this data. It's connected to other information.");
      }
      setopenAlert(true);
    } catch (error) {
      console.error('Error deleting Blog:', error);
      setAlertType('error');
      setAlertMessage('Failed to delete the Blog.');
      setopenAlert(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Clean the description before validation
    const cleanedDescription = cleanHtmlContent(BlogDescription);
    setBlogDescription(cleanedDescription);

    const formState = {
      BlogTitle,
      Category,
      selectedImageFile,
      BlogDescription: cleanedDescription,
      Summary,
      IsScheduled,
      ScheduledDateTime,
    };

    const validationErrors = validateAllFields(formState);
    setErrors(validationErrors);

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(validationErrors).forEach((key) => {
      allTouched[key] = true;
    });
    setTouchedFields((prev) => ({ ...prev, ...allTouched }));

    if (Object.keys(validationErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    const imageValue =
      selectedImageFile instanceof File
        ? selectedImageFile
        : operation === 'Edit'
        ? existingImageName || selectedImageFile || ''
        : selectedImageFile || '';

    formData.append('BlogTitle', BlogTitle);
    formData.append('Category', Category);
    formData.append('ProductID', ProductID || '');
    formData.append('BlogFileName', imageValue);
    formData.append('BlogDescription', cleanedDescription);
    formData.append('Summary', Summary);
    formData.append('ActiveStatus', ActiveStatus);
    formData.append('IsScheduled', IsScheduled);
    formData.append('ScheduledDateTime', ScheduledDateTime || '');
    formData.append(operation === 'Edit' ? 'ModifyedBy' : 'CreatedBy', 5);

    try {
      if (operation === 'Add') {
        await InsertBlog(formData);
      } else {
        await UpdateBlog(editID, formData);
      }
      resetFields();
      handleClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOpen = () => {
    resetFields();
    setOpen(true);
    setOperation('Add');
  };

  const handleEdit = (user) => {
    resetFields();
    setTouchedFields({});
    setErrors({});
    setEditID(user.BlogID);
    setBlogTitle(user.BlogTitle || '');
    setCategory(user.Category || '');
    setProductID(String(user.ProductID ?? ''));
    const imageName = user.BlogFileName || '';
    const imageUrl = imageName ? `${axios.baseURL}Blogs/${imageName}` : '';
    setExistingImageName(imageName);
    setSelectedImageFile(imageName);
    setImagePreview(imageUrl);
    // Clean the description when editing
    const cleanDescription = cleanHtmlContent(user.BlogDescription || '');
    setBlogDescription(cleanDescription);
    setSummary(user.Summary || '');
    setActiveStatus(user.ActiveStatus || 'Y');
    setIsScheduled(user.IsScheduled || 'N');
    setScheduledDateTime(user.ScheduledDateTime ? formatDateTimeLocal(user.ScheduledDateTime) : '');
    setOpen(true);
    setOperation('Edit');
  };

  const handleDelete = (courseId) => {
    setOpenDelete(true);
    setIdToDelete(courseId);
  };

  const handleDeleteConfirmed = () => {
    DeleteBlog(IdToDelete);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleCloseAlert = () => {
    setopenAlert(false);
  };

  const handleClose = () => {
    resetFields();
    setOpen(false);
    setIsEditorFullscreen(false);
    if (editorWrapperRef.current) {
      editorWrapperRef.current.classList.remove('fullscreen-editor');
    }
    document.body.style.overflow = '';
  };

  const handleImagePreview = (imageUrl, title) => {
    if (!imageUrl || imageUrl === NoimageFound) return;
    setPreviewImageUrl(imageUrl);
    setPreviewImageTitle(title || 'Blog Image');
    setPreviewImageOpen(true);
  };

  const handleCloseImagePreview = () => {
    setPreviewImageOpen(false);
    setPreviewImageUrl('');
    setPreviewImageTitle('');
  };

  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleEditorFullscreen = () => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;
    const nextState = !isEditorFullscreen;
    wrapper.classList.toggle('fullscreen-editor', nextState);
    setIsEditorFullscreen(nextState);
    document.body.style.overflow = nextState ? 'hidden' : '';
  };

  // Filtering logic with useMemo for performance
  const filteredData = useMemo(() => {
    let filtered = blogData;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((blog) =>
        Object.values(blog).some((value) => typeof value === 'string' && value.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (activeFilter && activeFilter.value !== 'all') {
      filtered = filtered.filter((blog) => blog.ActiveStatus === activeFilter.value);
    }

    // Schedule filter
    if (scheduleFilter && scheduleFilter.value !== 'all') {
      filtered = filtered.filter((blog) => {
        if (scheduleFilter.value === 'scheduled') return blog.IsScheduled === 'Y';
        if (scheduleFilter.value === 'unscheduled') return blog.IsScheduled === 'N';
        return true;
      });
    }

    // Date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((blog) => {
        const blogDate = new Date(blog.CreatedOn || blog.ScheduledDateTime || blog.ModifiedOn);
        return blogDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((blog) => {
        const blogDate = new Date(blog.CreatedOn || blog.ScheduledDateTime || blog.ModifiedOn);
        return blogDate <= toDate;
      });
    }

    return filtered;
  }, [blogData, searchTerm, activeFilter, scheduleFilter, dateFrom, dateTo]);

  const clearAllFilters = () => {
    setActiveFilter(null);
    setScheduleFilter(null);
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    handleFilterClose();
  };

  const [openCollapse, setOpenCollapse] = useState({});
  const toggleCollapse = useCallback((id) => {
    setOpenCollapse((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const getActiveFilterCount = useMemo(() => {
    let count = 0;
    if (activeFilter && activeFilter.value !== 'all') count += 1;
    if (scheduleFilter && scheduleFilter.value !== 'all') count += 1;
    if (dateFrom) count += 1;
    if (dateTo) count += 1;
    if (searchTerm) count += 1;
    return count;
  }, [activeFilter, scheduleFilter, dateFrom, dateTo, searchTerm]);

  // Render functions
  const renderCardView = () => (
    <Grid container spacing={3}>
      {filteredData.map((user) => {
        const imageUrl = user.BlogFileName ? `${axios.baseURL}Blogs/${user.BlogFileName}` : NoimageFound;
        return (
          <Grid item xs={12} sm={6} md={4} key={user.BlogID}>
            <Fade in timeout={400}>
              <BlogCard>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={imageUrl}
                    alt={user.BlogTitle}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => handleImagePreview(imageUrl, user.BlogTitle)}
                  />
                  <BlogOverlay className="blog-overlay">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(user);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(user.BlogID);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Show Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapse(user.BlogID);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <ArrowDropDownCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </BlogOverlay>
                  <Chip
                    label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                    color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontWeight: 600,
                      zIndex: 3,
                    }}
                  />
                  {user.ShortCode && (
                    <Chip
                      label={user.ShortCode}
                      size="small"
                      variant="outlined"
                      sx={{
                        position: 'absolute',
                        top: 48,
                        right: 12,
                        fontWeight: 700,
                        zIndex: 3,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: '#1e293b',
                        borderColor: 'rgba(15,23,42,0.15)',
                      }}
                    />
                  )}
                  {user.IsScheduled === 'Y' && (
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                      label="Scheduled"
                      color="warning"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: 12,
                        fontWeight: 600,
                        zIndex: 3,
                      }}
                    />
                  )}
                </Box>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>
                      {user.BlogTitle}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="caption" color="text.secondary">
                      {user.Category}
                    </Typography>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ReactHtmlParser(user.Summary || user.BlogDescription?.substring(0, 120))}
                  </Typography>
                </CardContent>
                <Collapse in={openCollapse[user.BlogID]} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      <strong>Full Description:</strong>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 120,
                        overflow: 'auto',
                        fontSize: '0.85rem',
                      }}
                    >
                      {ReactHtmlParser(user.BlogDescription)}
                    </Typography>
                    {user.Summary && (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          <strong>Summary:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {user.Summary}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Collapse>
              </BlogCard>
            </Fade>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderTableView = () => (
    <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e8ecf0' }}>
      <StickyTableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell align="center" sx={{ width: 40 }}>
                #
              </StyledTableCell>
              <StyledTableCell>Blog</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 80 }}>
                Status
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 90 }}>
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((user, index) => {
              const imageUrl = user.BlogFileName ? `${axios.baseURL}Blogs/${user.BlogFileName}` : NoimageFound;
              return (
                <StyledTableRow key={user.BlogID} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          p: 0.5,
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleImagePreview(imageUrl, user.BlogTitle)}
                      >
                        <img
                          src={imageUrl}
                          alt={user.BlogTitle}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                          {user.BlogTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {user.Category}
                          {user.ShortCode && (
                            <Chip
                              label={user.ShortCode}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 0.5, height: 18, fontSize: '0.55rem', fontWeight: 700 }}
                            />
                          )}
                          {user.IsScheduled === 'Y' && (
                            <Chip
                              icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                              label="Scheduled"
                              size="small"
                              color="warning"
                              sx={{ ml: 0.5, height: 18, fontSize: '0.55rem' }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Chip
                      icon={
                        user.ActiveStatus === 'Y' ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <HighlightOffIcon sx={{ fontSize: 14 }} />
                        )
                      }
                      label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                      color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600, height: 22, fontSize: '0.65rem' }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(user)} sx={{ color: 'primary.main', p: 0.5 }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user.BlogID)}
                        sx={{ color: 'error.main', p: 0.5 }}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={openCollapse[user.BlogID] ? 'Hide Details' : 'Show Details'}>
                      <IconButton
                        size="small"
                        onClick={() => toggleCollapse(user.BlogID)}
                        sx={{ color: 'success.main', p: 0.5 }}
                      >
                        {openCollapse[user.BlogID] ? (
                          <DoNotDisturbOnIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <ArrowDropDownCircleIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StickyTableContainer>
    </Paper>
  );

  return (
    <>
      <style>{`
        .blog-editor-wrapper {
          position: relative;
        }
        .blog-editor .ql-container {
          min-height: 200px;
          max-height: 60vh;
          overflow-y: auto;
        }
        .blog-editor .ql-editor {
          white-space: pre-wrap;
          min-height: 150px;
        }
        .blog-editor .ql-editor p {
          margin: 0 0 0.75rem;
        }
        .blog-editor .ql-editor h1, 
        .blog-editor .ql-editor h2, 
        .blog-editor .ql-editor h3, 
        .blog-editor .ql-editor h4 {
          margin-top: 0;
          margin-bottom: 0.5rem;
        }
        .blog-editor .ql-editor h1:first-child,
        .blog-editor .ql-editor h2:first-child,
        .blog-editor .ql-editor h3:first-child,
        .blog-editor .ql-editor h4:first-child {
          margin-top: 0;
        }
        .blog-editor-wrapper.fullscreen-editor {
          position: fixed;
          inset: 20px;
          background: #fff;
          z-index: 1400;
          padding: 12px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.22);
          border-radius: 12px;
        }
        .blog-editor-wrapper.fullscreen-editor .blog-editor {
          height: calc(100vh - 120px);
        }
        .blog-editor-wrapper.fullscreen-editor .ql-container {
          height: calc(100vh - 180px);
          max-height: none;
        }
        /* Sticky Dialog Styles */
        .sticky-dialog .MuiDialog-paper {
          display: flex;
          flex-direction: column;
          max-height: 95vh;
        }
        .sticky-dialog .MuiDialogContent-root {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 24px;
        }
        .sticky-dialog .MuiDialogActions-root {
          flex-shrink: 0;
        }
        .sticky-dialog .MuiDialogTitle-root {
          flex-shrink: 0;
        }
      `}</style>
      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseAlert} severity={alertType} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
        {/* Header */}
        <HeaderPaper elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ImageIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight={700} color="white" sx={{ letterSpacing: '0.02em' }}>
                Manage Blogs
              </Typography>
            </Stack>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Total: {blogData.length} blogs
            </Typography>
          </Stack>
        </HeaderPaper>

        {/* Search & Actions Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <TextField
            placeholder="Search blogs..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { md: 200 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={getActiveFilterCount} color="primary" invisible={getActiveFilterCount === 0}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Badge>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.5 } }}
            >
              <ToggleButton value="grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={handleAddOpen}
              sx={{ borderRadius: 2, boxShadow: 2 }}
            >
              New Blog
            </Button>
          </Stack>
        </Stack>

        {/* Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              maxWidth: 320,
              width: '100%',
              p: 2,
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Filter Blogs
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={filterOptions.status}
              getOptionLabel={(option) => option.label}
              value={activeFilter}
              onChange={(e, newValue) => setActiveFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Status" size="small" />}
            />

            <Autocomplete
              size="small"
              options={filterOptions.schedule}
              getOptionLabel={(option) => option.label}
              value={scheduleFilter}
              onChange={(e, newValue) => setScheduleFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Schedule" size="small" />}
            />

            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
              Date Range
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                type="date"
                label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" fullWidth onClick={clearAllFilters} startIcon={<ClearIcon />}>
                Clear All
              </Button>
              <Button variant="outlined" size="small" fullWidth onClick={handleFilterClose}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Results count */}
        {filteredData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Showing {filteredData.length} blog{filteredData.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {/* Content */}
        {filteredData.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No blogs found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or create a new blog
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          renderCardView()
        ) : (
          renderTableView()
        )}
      </Container>
      {/* Fullscreen Image Preview */}
      <FullscreenImageDialog
        open={previewImageOpen}
        imageUrl={previewImageUrl}
        onClose={handleCloseImagePreview}
        title={previewImageTitle}
      />
      {/* Add/Edit Dialog - Redesigned with Sticky Header & Footer */}
      {/* Add/Edit Dialog - Redesigned with Sticky Header & Footer */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullScreen={isMobile}
        fullWidth
        className="sticky-dialog"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Sticky Header */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              py: 2,
              pr: 2,
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {`${operation} Blog`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* Scrollable Content */}
          <DialogContent
            sx={{
              p: 3,
              overflow: 'auto',
              flex: '1 1 auto',
              '&:first-of-type': { pt: 3 },
            }}
          >
            <Grid container spacing={2}>
              {/* Image Upload - Left Column */}
              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      border: `2px dashed ${
                        errors.selectedImageFile && touchedFields.selectedImageFile ? '#d32f2f' : '#e0e0e0'
                      }`,
                      borderRadius: 2,
                      p: 1,
                      width: '100%',
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                      <img
                        src={selectedImageFile && imagePreview ? imagePreview : imagepreview}
                        alt="upload"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          objectFit: 'contain',
                        }}
                      />
                      <input
                        id="fileInput"
                        type="file"
                        accept="image/jpeg, image/png, image/jpg"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </Box>
                  {errors.selectedImageFile && touchedFields.selectedImageFile && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.selectedImageFile}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Form Fields - Right Column */}
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Title"
                      name="BlogTitle"
                      value={BlogTitle}
                      onChange={(e) => handleFieldChange('BlogTitle', e.target.value)}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={!!errors.BlogTitle && touchedFields.BlogTitle}
                      helperText={touchedFields.BlogTitle ? errors.BlogTitle : ''}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Category"
                      name="Category"
                      value={Category}
                      onChange={(e) => handleFieldChange('Category', e.target.value)}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={!!errors.Category && touchedFields.Category}
                      helperText={touchedFields.Category ? errors.Category : ''}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Product (Optional)"
                      name="ProductID"
                      value={ProductID || ''}
                      onChange={(e) => handleFieldChange('ProductID', e.target.value)}
                      fullWidth
                      size="small"
                      displayEmpty
                      SelectProps={{
                        renderValue: (selected) => {
                          if (!selected) return 'Select Product';
                          const selectedProduct = productList.find(
                            (product) => String(product.ProductID) === String(selected)
                          );
                          return selectedProduct
                            ? `${selectedProduct.ProductName} (${selectedProduct.ShortCode})`
                            : 'Select Product';
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Product</em>
                      </MenuItem>
                      {productList.map((product) => (
                        <MenuItem key={product.ProductID} value={product.ProductID}>
                          {product.ProductName} ({product.ShortCode})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>

              {/* Blog Content - Full Width */}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Blog Content *
                  </Typography>
                  <IconButton onClick={toggleEditorFullscreen} size="small">
                    {isEditorFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Stack>
                <div ref={editorWrapperRef} className="blog-editor-wrapper">
                  {isEditorFullscreen && (
                    <IconButton
                      onClick={toggleEditorFullscreen}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        bgcolor: 'rgba(17,24,39,0.75)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(17,24,39,0.9)' },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <ReactQuill
                    value={BlogDescription}
                    onChange={handleChange2}
                    theme="snow"
                    modules={quillModules}
                    className="blog-editor"
                  />
                </div>
                {errors.BlogDescription && touchedFields.BlogDescription && (
                  <Typography variant="caption" color="error">
                    {errors.BlogDescription}
                  </Typography>
                )}
              </Grid>

              {/* Summary */}
              <Grid item xs={12}>
                <TextField
                  label="Summary"
                  name="Summary"
                  multiline
                  rows={3}
                  value={Summary}
                  onChange={(e) => handleFieldChange('Summary', e.target.value)}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  error={!!errors.Summary && touchedFields.Summary}
                  helperText={touchedFields.Summary ? errors.Summary : ''}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>

          {/* Sticky Footer with Switches, DateTime Picker, and Buttons */}
          <DialogActions
            sx={{
              p: 2,
              borderTop: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              gap: 2,
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Left side - Status Switch */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ActiveStatus === 'Y'}
                      onChange={(e) => handleFieldChange('ActiveStatus', e.target.checked ? 'Y' : 'N')}
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            {/* Center - Schedule Switch with DateTime Picker */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={IsScheduled === 'Y'}
                      onChange={(e) => handleFieldChange('IsScheduled', e.target.checked ? 'Y' : 'N')}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
                      {IsScheduled === 'Y' ? 'Scheduled' : 'Not Scheduled'}
                    </Typography>
                  }
                />
              </FormGroup>

              {/* Schedule DateTime Picker - appears when scheduled is enabled */}
              {IsScheduled === 'Y' && (
                <TextField
                  type="datetime-local"
                  size="small"
                  value={ScheduledDateTime}
                  onChange={(e) => handleFieldChange('ScheduledDateTime', e.target.value)}
                  sx={{
                    minWidth: 200,
                    maxWidth: 250,
                    '& .MuiInputBase-root': {
                      height: 36,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.ScheduledDateTime && touchedFields.ScheduledDateTime}
                  helperText={touchedFields.ScheduledDateTime ? errors.ScheduledDateTime : ''}
                  required={IsScheduled === 'Y'}
                />
              )}
            </Stack>

            {/* Right side - Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleClose} size="medium">
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting} size="medium">
                {isSubmitting ? 'Saving...' : operation === 'Add' ? 'Create Blog' : 'Update Blog'}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Blog?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Loader */}
      <Dialog open={openLoader} fullWidth>
        <LinearProgress />
      </Dialog>
    </>
  );
};

export default Blogs;
